import type { TFunction } from 'i18next';

import { convertToSeconds, offsetInMsToString } from './format-time';

/**
 * Validates a single time update for a cue point.
 * @param index - The index of the cue point being validated
 * @param values - The list of all cue points
 * @param t - The translation function
 * @returns An object containing the validation results
 */
export function validateSingleTimeUpdate(
  index: number,
  values: {
    time: string;
    error?: string;
  }[],
  t: TFunction
): { values: { time: string; error?: string }[]; hasError: boolean } {
  const value = values[index].time;

  const offsetInSeconds = convertToSeconds(value);
  if (offsetInSeconds.error) {
    values[index] = { ...values[index], error: t('manager.eventSummary.cuePoints.validation.wrongFormatHHMMSS') };

    return {
      values,
      hasError: true,
    };
  }

  if (offsetInSeconds.value < 0) {
    values[index] = { ...values[index], error: t('manager.eventSummary.cuePoints.validation.mustBePositive') };

    return {
      values,
      hasError: true,
    };
  }

  const result = validateValues(
    values.map((value) => convertToSeconds(value.time).value),
    t
  );

  const validatedValues = result.values.map((value) => ({
    time: offsetInMsToString(value.time * 1000, true),
    error: value.error,
  }));

  return { values: validatedValues, hasError: result.hasError };
}

/**
 * Validates a bulk time update for cue points.
 * @param values - The list of cue points with their checked status and time values
 * @param bulkOffsetShift - The bulk offset shift value
 * @param factor - The factor by which to adjust the time
 * @param t - The translation function
 * @returns An object containing the validation results
 */
export function validateBulkUpdate(
  values: {
    checked: boolean;
    time: string;
    error?: string;
  }[],
  bulkOffsetShift: string,
  direction: 1 | -1,
  t: TFunction
) {
  const offsetInSeconds = convertToSeconds(bulkOffsetShift, 'mm:ss');

  const slideMarkerTimes = values.map((value) => {
    const currentTime = convertToSeconds(value.time).value;
    const newTime = value.checked ? currentTime + offsetInSeconds.value * direction : currentTime;
    return newTime;
  });

  const result = validateValues(slideMarkerTimes, t);

  const validatedValues = result.values.map((value) => ({
    time: offsetInMsToString(value.time * 1000, true),
    error: value.error,
  }));

  return { values: validatedValues, hasError: result.hasError };
}

function validateValues(values: number[], t: TFunction) {
  let hasError = false;
  const newSlideTimeValues = values.map((value, index) => {
    let error: string | undefined;
    if (index < values.length - 1 && values[index] >= values[index + 1]) {
      error = t('manager.eventSummary.cuePoints.validation.mustBeLessThanNext');
      hasError = true;
    }

    if (values[index] < 0) {
      error = t('manager.eventSummary.cuePoints.validation.mustBePositive');
      hasError = true;
    }

    return { time: value, error };
  });

  return {
    values: newSlideTimeValues,
    hasError,
  };
}
